import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useCallback, useContext, useEffect, useState } from "react";
import { RootStoreContext } from "src/stores/RootStore";
import { UI } from "@wwimmo/ui";
import styles from "./AgendaVotingProgressModal.module.css";
import { IVotingBasis, QuorumType } from "src/stores/assemblies/VotingStore";

const AgendaVotingInformationTitle = observer(function AgendaVotingInformationTitle() {
    const { t } = useTranslation();
    const { votingStore, assemblyStore } = useContext(RootStoreContext);

    const updateVotingFields =
        <K extends keyof IVotingBasis>(field: K) =>
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const value =
                typeof votingStore.votingBasis[field] === "boolean"
                    ? (e.target as HTMLInputElement).checked
                    : e.target.value;

            votingStore.updateVotingBasisField(field, value as IVotingBasis[K]);
        };

    return (
        <UI.Input
            label={`${t("labels.title")} *`}
            onChange={updateVotingFields("title")}
            value={votingStore.votingBasis.title}
            disabled={assemblyStore.isAssemblyReadOnly}
        />
    );
});

const AgendaVotingInformationDropdown = observer(function AgendaVotingInformationDropdown() {
    const { t } = useTranslation();
    const { votingStore, assemblyStore } = useContext(RootStoreContext);
    // TODO: Veto for managers is currently hidden by request. Set to false to show it again.
    const hideVetoForManager = true;
    const isLocked = assemblyStore.isAssemblyReadOnly;

    const [isMajorityDropdownSelected, setMajorityDropdownSelected] = useState(false);

    const updateVotingFields =
        <K extends keyof IVotingBasis>(field: K) =>
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const value =
                typeof votingStore.votingBasis[field] === "boolean"
                    ? (e.target as HTMLInputElement).checked
                    : e.target.value;

            votingStore.updateVotingBasisField(field, value as IVotingBasis[K]);
        };

    const onClickMajorityLabel = () => {
        if (isLocked) return;
        setMajorityDropdownSelected((prev) => !prev);
    };

    const onBlurMajority = (e: React.FocusEvent<HTMLDivElement>) => {
        // element that gets focus next (can be null if clicking non-focusable stuff)
        const next = e.relatedTarget as Node | null;

        // If focus is still inside the majority block, do nothing
        if (next && e.currentTarget.contains(next)) return;

        setMajorityDropdownSelected(false);
    };

    const handleMajorityPresetSelect = (val: number) => {
        if (isLocked) return;
        votingStore.updateVotingBasisField("majority", val as any);
        setMajorityDropdownSelected(false);
    };

    const handleQuorumSelect = useCallback(
        (value: string) => {
            if (isLocked) return;
            votingStore.updateVotingBasisField("quorum", parseInt(value, 10));

            if (
                votingStore.votingBasis.quorum === QuorumType.QUALIFIED_MAJORITY &&
                votingStore.keys.length > 0 &&
                votingStore.votingBasis.key2 === ""
            ) {
                votingStore.updateVotingBasisField("key2", votingStore.keys[0].keyid);
            }

            if (votingStore.votingBasis.quorum === QuorumType.UNANIMITY) {
                votingStore.updateVotingBasisField("majority", 100);
            } else {
                votingStore.updateVotingBasisField("majority", 51);
            }
        },
        [votingStore, isLocked]
    );

    return (
        <div className={styles.QuorumVetoRow}>
            <div className={styles.QuorumDiv}>
                <label className={styles.Label}>{`${t("screens.assemblies.vote.quorum")} *`}</label>
                <div style={isLocked ? { pointerEvents: "none", opacity: 0.6 } : undefined}>
                    <UI.Dropdown
                        id="quorum-select"
                        onSelect={handleQuorumSelect}
                        items={Array.from(votingStore.quorums.entries()).map(([key, { label }]) => ({
                            label: label,
                            value: key.toString()
                        }))}
                        label={votingStore.quorums.get(votingStore.votingBasis.quorum)?.label ?? ""}
                        className="mb-2"
                    />
                </div>
            </div>

            {/* Majority */}
            <div className={styles.Majority} tabIndex={-1} onBlur={onBlurMajority}>
                <label className={styles.Label} onClick={onClickMajorityLabel}>
                    {t("screens.assemblies.vote.majority").toString()}
                    <UI.Icon
                        className={styles.ArrowIcon}
                        icon={isMajorityDropdownSelected ? UI.SVGIcon.ArrowUpDropdown : UI.SVGIcon.ArrowDownDropdown}
                    />
                </label>

                <div className={styles.MajorityInputWrapper}>
                    {isMajorityDropdownSelected && (
                        <div className={styles.MajorityDropdownList} role="listbox">
                            <button
                                type="button"
                                className={styles.MajorityDropdownItem}
                                onClick={() => handleMajorityPresetSelect(33.33)}
                            >
                                1/3
                            </button>
                            <button
                                type="button"
                                className={styles.MajorityDropdownItem}
                                onClick={() => handleMajorityPresetSelect(66.66)}
                            >
                                2/3
                            </button>
                        </div>
                    )}

                    <UI.Input
                        type="number"
                        value={votingStore.votingBasis.majority}
                        onChange={updateVotingFields("majority")}
                        disabled={isLocked || votingStore.votingBasis.quorum === QuorumType.UNANIMITY}
                        className={styles.MajorityInput}
                    />
                    <span className={styles.PercentSymbol}>%</span>
                </div>

                {votingStore.validationErrorMajority && (
                    <span className={styles.ValidationError}>{votingStore.validationErrorMajority}</span>
                )}
            </div>

            <UI.Checkbox
                name="veto"
                label={t("screens.assemblies.vote.veto")}
                checked={votingStore.votingBasis.veto}
                onChange={updateVotingFields("veto")}
                disabled={isLocked}
                className={`${styles.Veto} ${hideVetoForManager ? styles.HiddenVeto : ""}`}
            />
            <UI.Checkbox
                name="entirety"
                label={t("screens.assemblies.vote.entirety")}
                checked={votingStore.votingBasis.entirety}
                onChange={updateVotingFields("entirety")}
                disabled={isLocked}
                className={styles.Veto}
            />
        </div>
    );
});

const AgendaVotingInformationQuestion = observer(function AgendaVotingInformationQuestion() {
    const { t } = useTranslation();
    const { votingStore, assemblyStore } = useContext(RootStoreContext);

    const updateVotingFields =
        <K extends keyof IVotingBasis>(field: K) =>
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const value =
                typeof votingStore.votingBasis[field] === "boolean"
                    ? (e.target as HTMLInputElement).checked
                    : e.target.value;

            votingStore.updateVotingBasisField(field, value as IVotingBasis[K]);
        };

    return (
        <UI.Input
            label={`${t("labels.question")} *`}
            as="textarea"
            onChange={updateVotingFields("question")}
            value={votingStore.votingBasis.question}
            disabled={assemblyStore.isAssemblyReadOnly}
        />
    );
});

const AgendaVotingInformationVotingKeys = observer(function AgendaVotingInformationVotingKeys() {
    const { t } = useTranslation();
    const { votingStore, assemblyStore } = useContext(RootStoreContext);
    const isLocked = assemblyStore.isAssemblyReadOnly;

    const handleKey1Select = useCallback(
        (value: string) => {
            if (isLocked) return;
            votingStore.updateVotingBasisField("key1", value);
        },
        [votingStore, isLocked]
    );

    const handleKey2Select = useCallback(
        (value: string) => {
            if (isLocked) return;
            votingStore.updateVotingBasisField("key2", value);
        },
        [votingStore, isLocked]
    );

    return (
        <>
            <div className={styles.KeyDiv}>
                <label className={styles.Label}>{`${t("screens.assemblies.vote.voting_keys")} *`}</label>
                <div style={isLocked ? { pointerEvents: "none", opacity: 0.6 } : undefined}>
                    <UI.Dropdown
                        id="key-select"
                        onSelect={handleKey1Select}
                        items={votingStore.keys.map((item) => ({
                            label: item.name,
                            value: item.keyid
                        }))}
                        label={votingStore.keys.find((k) => k.keyid === votingStore.votingBasis.key1)?.name ?? ""}
                        className="mb-2"
                    />
                </div>
            </div>
            {votingStore.votingBasis.quorum === QuorumType.QUALIFIED_MAJORITY ? (
                <div className={styles.KeyDiv}>
                    <div style={isLocked ? { pointerEvents: "none", opacity: 0.6 } : undefined}>
                        <UI.Dropdown
                            id="key-select"
                            onSelect={handleKey2Select}
                            items={votingStore.keys.map((item) => ({
                                label: item.name,
                                value: item.keyid
                            }))}
                            label={votingStore.keys.find((k) => k.keyid === votingStore.votingBasis.key2)?.name ?? ""}
                            className="mb-2"
                        />
                    </div>
                </div>
            ) : undefined}
        </>
    );
});

export const AgendaVotingInformation = observer(function AgendaVotingInformation() {
    const { votingStore } = useContext(RootStoreContext);
    const { keys, votingBasis, updateVotingBasisField, isNewVoting, votingList, selectedVotingId } = votingStore;

    useEffect(() => {
        if (keys.length > 0 && votingBasis.key1 === "") {
            updateVotingBasisField("key1", keys[0].keyid);
        }
    }, [keys, votingBasis.key1, votingBasis.key2, updateVotingBasisField]);

    useEffect(() => {
        if (!isNewVoting) {
            const existingVoting = votingList.find((voting) => voting.votingid === selectedVotingId);
            if (existingVoting) {
                updateVotingBasisField("title", existingVoting.title);
                updateVotingBasisField("question", existingVoting.question);
            }
        }
    }, [isNewVoting, selectedVotingId, votingList, updateVotingBasisField]);

    return (
        <UI.Col>
            <AgendaVotingInformationTitle />
            <AgendaVotingInformationQuestion />
            <AgendaVotingInformationDropdown />
            <AgendaVotingInformationVotingKeys />
        </UI.Col>
    );
});
